import ReactDOM from 'react-dom'

import { constants } from './lazy-select.constant'
import * as lazySelectHelpers from './lazy-select.helper'
import { LazySelectData } from './lazy-select.model'
import * as lazySelectSelectors from './lazy-select.selector'
import LazySelect from './LazySelect'

const LazySelectComponent = (() => {
	const renderLazySelect = (renderTarget: HTMLElement, target: HTMLSelectElement, label: string) => {
		const url = target.getAttribute(constants.attribute.url)
		if (url === null) {
			return
		}

		const limitAttr = target.getAttribute(constants.attribute.limit)
		const limit = limitAttr !== null ? parseInt(limitAttr, 10) : undefined

		ReactDOM.render(
			<LazySelect
				id={target.getAttribute('id') || ''}
				label={label}
				url={url}
				limit={limit}
				multiple={target.multiple}
				disabled={target.disabled}
				defaultValue={lazySelectHelpers.getDefaultValueFromNativeSelect(target)}
				placeholder={target.getAttribute(constants.attribute.placeholder) || undefined}
				onChange={handleSelectChange}
			/>,
			renderTarget,
		)
	}

	const setDynamicOption = (target: HTMLSelectElement, value: LazySelectData) => {
		let option = lazySelectSelectors.getDynamicOptionWithValue(value.value, target)

		if (option === undefined) {
			option = document.createElement('option')
			option.setAttribute(constants.selector.dynamicOptionAttribute, '')
			option = target.appendChild(option)
		}

		option.value = value.value
		option.text = value.label
		option.selected = true
	}

	const handleSelectChange = (id: string, value: LazySelectData | LazySelectData[]) => {
		const input = document.getElementById(id) as HTMLSelectElement | null
		if (input === null) {
			return
		}

		const options = input.querySelectorAll<HTMLOptionElement>('option')
		options.forEach((option) => {
			option.selected = false
		})

		lazySelectSelectors.getDynamicOptions(input).forEach((option) => option.remove())

		const valuesArray: LazySelectData[] = Array.isArray(value) ? value : value.value !== '' ? [value] : []

		valuesArray.forEach((item) => {
			if (item.value !== '') {
				setDynamicOption(input, item)
			}
		})

		if (!input.multiple) {
			const single = Array.isArray(value) ? value[0] : value
			input.value = single && single.value !== '' ? single.value : ''
		}

		input.dispatchEvent(new window.CustomEvent('change'))
	}

	const createElementForRender = () => {
		const fragment = document.createDocumentFragment()
		const div = document.createElement('div')
		div.setAttribute(constants.selector.render, '')
		fragment.append(div)
		return {
			fragment,
			div,
		}
	}

	const initLazySelect = (target: HTMLSelectElement) => {
		const parent = target.parentNode as HTMLElement | null
		const id = target.getAttribute('id')
		if (parent === null || id === null) {
			return
		}

		const { fragment, div } = createElementForRender()
		const label = lazySelectSelectors.getLabel(parent)
		const render = lazySelectSelectors.getRender(parent)

		if (render) {
			renderLazySelect(render, target, label?.textContent || '')
			return
		}

		parent.appendChild(fragment)
		renderLazySelect(div, target, label?.textContent || '')

		if (label !== null) {
			label.hidden = true
		}
	}

	const initLazySelects = () => {
		const targets = lazySelectSelectors.getTargets()
		for (let i = 0, { length } = targets; i < length; i++) {
			initLazySelect(targets[i])
		}
	}

	const init = () => {
		initLazySelects()
	}

	return {
		init,
		initLazySelect,
	}
})()

export default LazySelectComponent
